<script setup lang="ts">
	import type { TableRowSelection } from 'ant-design-vue/lib/table/interface'
	import useEmployeesApi from '~/services/apis/employeesApi'
	import usePurchaseApi from '~/services/apis/purchases-info'
	import type { EmployeesActionKeys } from '~/pages/employees/index.vue'
	import type { RecordSchema } from '@/types'

	const props = defineProps<{
		rowSelection?: TableRowSelection
		filter?: RecordSchema
	}>()
	defineEmits<{
		(event: 'upload-staff'): void
	}>()

	const { hasAnyPermission } = reactor(usePermissionStore())
	const { employeesTableColumn } = useStore()
	const { allEmployees } = useEmployeesApi()
	const { getDeactivatedPolicies, getPolicies } = usePurchaseApi()
	const { urlQuery } = useUtils()
	const { formatDate } = useCustomDate()
	const { internationalNoFormat } = useCurrency()
	const tableColumnList = [...employeesTableColumn]
	const currentTab = computed(() => String(urlQuery.value?.tab ?? 'staff-collection') as EmployeesActionKeys)
	const isPolicyRecord = computed(() => String(currentTab.value).includes('policies'))
	const tips = computed<Record<string, string>>(() => ({
		active_staff: 'These are staff that have been added to a plan',
		inactive_staff: 'These are staff that have not been added to any plan',
		deactivated_policies: 'These are policies that have been deactivated from plans attached to them'
	}))
	const tableOptions = computed<TableFilterOptions>(() => ({
		cache: 0,
		filter: { ...props.filter },
		rowSelection: props.rowSelection,
		watch: [currentTab.value]
	}))
	const tableColumns = computed(() => {
		if (currentTab.value === 'policies') {
			const list = [...tableColumnList]

			list.splice(4, 1, {
				dataIndex: 'policy_number',
				title: 'Policy Number',
				key: 'policy_id'
			})
			list.splice(5, 1, {
				dataIndex: ['product', 'name'],
				title: 'Plan',
				key: 'plan'
			})
			list.splice(6, 0, {
				dataIndex: 'policy_expiration_date',
				title: 'Expiry Date',
				key: 'expiry_date'
			})
			return list
		}
		if (['deactivated-policies', 'inactive-staff', 'policies'].includes(currentTab.value)) {
			// @ts-ignore
			return tableColumnList.filter(e => !['total_plans', 'active_plans'].includes(String(e.dataIndex)))
		}

		return tableColumnList.filter(e => e.dataIndex !== 'status')
	})

	const terminateCompute = (arg: string) => {
		const expiryDate: Date = new Date(arg)
		expiryDate.setDate(expiryDate.getDate() + 30)
		// Get today's date
		const today: Date = new Date()

		// Calculate the days left
		return Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 3600 * 24))
	}
	const fetchEmployeePolicy = async (arg: RecordSchema) => {
		if (String(currentTab.value)?.includes('deactivated')) {
			return await getDeactivatedPolicies(arg)
		} else if (currentTab.value === 'policies') {
			return await getPolicies(arg)
		}
		return await allEmployees({ is_active: currentTab.value === 'staff-collection', ...arg })
	}
</script>

<template>
	<div>
		<p class="text-sm font-medium text-gray-700">
			{{ tips?.[currentTab] ?? '' }}
		</p>

		<ui-data-table :columns="tableColumns" :records="fetchEmployeePolicy" :options="tableOptions">
			<template #bodyCell="{ column, record }">
				<slot :name="column?.key" :record="record">
					<span v-if="column?.key === 'enrolled_date'">
						{{ formatDate(record?.created_at) }}
					</span>
					<span v-if="column?.key === 'total_plans'">
						{{ internationalNoFormat(record?.total_policies_count ?? 0) }}
					</span>
					<span v-if="column?.key === 'active_plans'">
						{{ internationalNoFormat(record?.active_policies_count ?? 0) }}
					</span>
					<span v-if="column?.key === 'phone_number'">
						{{ record?.phone ?? record?.phone_number }}
					</span>
					<span v-if="column?.key === 'expiry_date'">
						{{ formatDate(record?.policy_expiration_date, 'DD, MMM YYYY') }}
					</span>
					<span v-if="column?.key === 'name'" class="capitalize">
						{{ record?.first_name || 'N/A' }}
						{{ record?.last_name || '' }}
					</span>
					<span v-if="column?.key === 'policy_id'" class="capitalize">
						<shared-click-to-copy
							v-slot="{ text }"
							:copy-text="record?.policy_number"
							:text="record?.policy_number"
						>
							{{ text || 'N/A' }}
						</shared-click-to-copy>
					</span>
					<span
						v-if="column?.key === 'terminate_date'"
						:class="{
							'text-warning':
								terminateCompute(record?.terminated_at || '') <= 15 &&
								terminateCompute(record?.terminated_at || '') > 3,
							'text-error-500': terminateCompute(record?.terminated_at || '') <= 3,
							'text-gray-600': terminateCompute(record?.terminated_at || '') >= 30
						}"
					>
						{{ terminateCompute(record?.terminated_at || '') }} day{{
							(terminateCompute(record?.terminated_at || '') > 1 && 's') || ''
						}}
					</span>
					<my-cover-plan-status
						v-if="column?.key === 'status' && currentTab.includes('policies')"
						:pending="!record?.policy_number"
						:plan-details="record || null"
					/>

					<shared-badge
						v-else-if="column?.key === 'status'"
						:text="
							currentTab.includes('inactive')
								? 'No plan yet'
								: !record?.is_deactivated
									? 'active'
									: 'deactivated'
						"
						:active="record?.is_active || record?.active"
						:class="{ 'bg-gray-100': currentTab.includes('inactive') }"
						class="text-xs font-medium"
					/>
					<span v-if="column?.key === 'details'" class="space-x-3">
						<span v-if="hasAnyPermission([PERMISSIONS.PLAN.VIEW_DETAILS])">
							<nuxt-link
								v-if="record?.id"
								:to="{
									name: !isPolicyRecord ? 'employees-employee_id' : 'mycover-custodian-custodian_id',
									params: {
										...(!isPolicyRecord
											? { employee_id: record?.customer_id ?? record.id }
											: { custodian_id: record?.meta?.policy_id ?? record?.id ?? '' })
									},
									query: {
										status: currentTab,
										employee_status: currentTab
									}
								}"
								class="px-4 py-3.5 font-medium text-primary-2 hover:text-primary-300"
							>
								View
							</nuxt-link>
							<span v-else class="text-xs font-medium italic text-gray-600">Not available</span>
						</span>
						<span v-else class="text-[10px] font-medium italic text-gray-600">permission required</span>
					</span>
				</slot>
			</template>
			<template #empty-cell>
				<shared-empty-state
					:use-button="true"
					title="No Staff"
					description="You do not have any employee added yet"
					class="pt-[100px]"
				>
					<template #icon>
						<lazy-tools-icon dir="other" icon="inactive_people" />
					</template>

					<template #button>
						<button
							class="mt-10 w-full rounded-lg bg-[#4FBFA3] px-[18px] py-[16px] font-medium text-white md:w-3/12 md:py-[10px]"
							@click="$emit('upload-staff')"
						>
							Upload Staff
						</button>
					</template>
				</shared-empty-state>
			</template>
		</ui-data-table>
	</div>
</template>
